import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaIdCard,
  FaShieldAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";

const VerifyPAN = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [panNumber, setPanNumber] = useState("");
  const [error, setError] = useState("");

  // Handle PAN input changes
  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase();
    setPanNumber(value);

    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  // Validate PAN format
  const validatePAN = () => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (!panNumber.trim()) {
      setError("PAN number is required");
      return false;
    }

    if (!panRegex.test(panNumber)) {
      setError("Please enter a valid PAN number (e.g., ABCDE1234F)");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePAN()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please enter a valid PAN number",
        timer: 2000,
      });
      return;
    }

    await verifyPAN();
  };

  // Verify PAN via API
  const verifyPAN = async () => {
    setLoading(true);

    try {
      // Call your PAN verification API here
      const result = await PPIDmtService.verifyPAN(
        data?.token,
        customerMobile,
        panNumber
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "PAN Verification Failed",
          text: result.message || "Unable to verify PAN",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message || "PAN verified successfully",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          // Navigate to the next screen with PAN verification data
          navigate("/ppi-money-transfer", {
            state: {},
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "PAN Verification Failed",
          text: res.message || "Invalid PAN or verification failed",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/ppi-money-transfer", {
      state: {},
    });
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0 text-white">
                <FaIdCard className="me-2" /> PAN Verification
              </h5>
            </div>
            <div className="card-body">
              <Form onSubmit={handleSubmit}>
                {/* PAN Number Input */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    <FaIdCard className="me-2" /> PAN Number
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaShieldAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={panNumber}
                      onChange={handlePanChange}
                      placeholder="Enter 10-character PAN (e.g., ABCDE1234F)"
                      maxLength={10}
                      isInvalid={!!error}
                      disabled={loading}
                      style={{ textTransform: "uppercase" }}
                    />
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {error}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Format: 5 letters + 4 numbers + 1 letter (e.g., ABCDE1234F)
                  </Form.Text>
                </Form.Group>

                {/* Action Buttons */}
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleGoBack}
                    disabled={loading}
                  >
                    <FaArrowLeft className="me-2" />
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Verifying PAN...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Verify PAN
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default VerifyPAN;
